import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { FEATURE_LABEL } from "@/components/billing/BuildPlan";

export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");
export const TIER = { pro: "Pro", commercial: "Commercial" };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const per = (iv) => (iv === "year" ? "year" : "month");

export const planName = (p) => {
  if (!p) return "Free";
  if (p.features) return `Custom (${p.features.map((f) => FEATURE_LABEL[f]).join(", ")}${p.tier === "commercial" ? " + commercial license" : ""})`;
  return TIER[p.tier] || "Pro";
};

// target: { tier: "free"|"pro"|"commercial"|"custom", interval, features?, commercial? }
export const ChangePlanDialog = ({ target, sub, onClose, onDone }) => {
  const toFree = target.tier === "free";
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const body = { tier: target.tier, interval: target.interval, features: target.features || [], commercial: !!target.commercial };
  useEffect(() => {
    if (toFree) return;
    api.post("/billing/change-plan/preview", body).then((r) => setPv(r.data)).catch((e) => setErr(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const confirm = async () => {
    setBusy(true);
    try {
      if (toFree) {
        await api.post("/billing/cancel");
        toast.success(`You'll keep your plan until ${fmtDate(sub?.current_period_end)}, then move to Free`);
      } else {
        const { data } = await api.post("/billing/change-plan", { ...body, proration_date: pv?.proration_date });
        toast.success(data.kind === "upgrade" ? "Upgraded. Your new plan is active now." : `Switch scheduled for ${fmtDate(pv?.effective_at)}`);
      }
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const newName = toFree ? "Free" : planName(pv?.new_plan);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="change-plan-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Switch to {toFree ? "Free" : pv ? newName : "…"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-[#D6CCBC]" data-testid="change-plan-summary">
          {err && <p className="text-[#F87171]" data-testid="change-plan-error">{err}</p>}
          {!toFree && !pv && !err && <p className="flex items-center gap-2 text-[#A8A090]"><Loader2 className="h-4 w-4 animate-spin" />Working out the exact amount…</p>}
          {toFree && <>
            <p>You keep <b>{planName(sub)}</b> until <b data-testid="change-plan-date">{fmtDate(sub?.current_period_end)}</b>, the end of the time you've already paid for.</p>
            <p>After that you move to Free, with no more charges. Your universes and writing stay safe. Pro tools become read-only.</p>
          </>}
          {pv?.kind === "upgrade" && <>
            <p>Your new plan starts <b>right away</b>.</p>
            <div className="rounded-md border border-[#2D3442] bg-[#0F131A] p-4">
              <div className="flex justify-between"><span>{newName}, billed {per(pv.new_plan.interval)}ly</span><span>{money(pv.new_price)}</span></div>
              <div className="mt-1 flex justify-between text-[#86EFAC]" data-testid="change-plan-credit"><span>Credit for unused time on {planName(sub)}</span><span>−{money(pv.credit)}</span></div>
              {pv.balance_credit > 0 && <div className="mt-1 flex justify-between text-[#86EFAC]"><span>Account credit</span><span>−{money(pv.balance_credit)}</span></div>}
              <div className="mt-2 flex justify-between border-t border-[#2D3442] pt-2 font-medium text-[#F3EBDD]"><span>Charged today</span><span data-testid="change-plan-amount">{money(pv.amount_due)}</span></div>
            </div>
            <p className="text-xs text-[#A8A090]">Then {money(pv.new_price)} per {per(pv.new_plan.interval)}. Any credit that's left over stays on your account and is used on future bills.</p>
          </>}
          {pv?.kind === "downgrade" && <>
            <p>You keep <b>{planName(sub)}</b> until <b data-testid="change-plan-date">{fmtDate(pv.effective_at)}</b>, so you get everything you've already paid for.</p>
            <p>Then you switch to <b>{newName}</b> at {money(pv.new_price)} per {per(pv.new_plan.interval)}. <b>Nothing is charged today.</b> You can undo this any time before then.</p>
          </>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="change-plan-cancel">Not now</Button>
          <Button onClick={confirm} disabled={busy || (!toFree && !pv)} data-testid="change-plan-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {toFree ? "Switch to Free at period end" : pv?.kind === "upgrade" ? `Pay ${money(pv.amount_due)} and upgrade` : "Schedule the switch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
